import { Brand } from '@sccs/types';
import { OneSearchVehicleTileDataParams } from '../../http/clients/onesearch/graphql/OneSearchGraphQL';
import { OneSearchPreferences } from '../../http/clients/onesearch/OneSearchPreference';
import { OneSearchParameter } from '../../types/GeneralParameter';
import { OneSearchBrandMap } from '../one-search/OneSearchBrandMap';
import { splitModelIdentifier } from '../SplitData';
import { vehicleCategoryCommercial, vehicleCategoryPrivate } from '../VansCompatMap';
import { createVanConfiguration } from '../VansConfiguration';

export function removeClassSuffixForOneSearch(modelIdentifiers: string): string {
  return modelIdentifiers.split('_')[0] ?? '';
}

export function removeClassSuffixFromMultipleIdentifier(modelIds: string | undefined): string {
  if (!modelIds) {
    return '';
  }
  const resultArray = modelIds.split(',');
  const processedArray = resultArray.map((modelId) => removeClassSuffixForOneSearch(modelId));
  const concatenatedResult = processedArray.join(',');
  return concatenatedResult;
}

export function createMandatoryParameter(parameter: OneSearchParameter) {
  const oneSearchFilterPrefix = `${parameter.market}-NEW_VEHICLES`;
  const defaultBrand = isOneSearchPreferenceType(parameter)
    ? ((parameter.brandFilters as Brand) ?? (['Mercedes-Benz'] as unknown as Brand))
    : ((parameter.brand as Brand) ?? (['Mercedes-Benz'] as unknown as Brand));
  const oneSearchCompatibleBrand = OneSearchBrandMap[defaultBrand];

  if (isOneSearchPreferenceType(parameter)) {
    return {
      brandFilters: oneSearchCompatibleBrand,
      oneSearchFilterPrefix,
    };
  }
  return {
    brand: oneSearchCompatibleBrand,
    oneSearchFilterPrefix,
  };
}

export function isOneSearchPreferenceType(parameter: OneSearchParameter): parameter is OneSearchPreferences {
  return (parameter as OneSearchPreferences).brandFilters !== undefined;
}

export function isOneSearchGraphQlType(
  parameter: OneSearchVehicleTileDataParams,
): parameter is OneSearchVehicleTileDataParams {
  return (parameter as OneSearchVehicleTileDataParams).profileId !== undefined;
}

export function getRequestConfiguration(isCommercialVan: boolean, parameter: OneSearchParameter): OneSearchParameter {
  if (isCommercialVan) {
    const vansConfig = createVanConfiguration(parameter);
    const { body } = splitModelIdentifier(vansConfig.modelIdentifier);
    const bodyType = vansConfig.bodyType ?? body;
    if ('vehicleCategory' in vansConfig) {
      if (isOneSearchPreferenceType(parameter)) {
        return {
          ...parameter,
          brandFilters: parameter.brandFilters,
          bodyTypeFilters: parameter.bodyTypeFilters ?? bodyType,
          fuelType: vansConfig.fuelType,
          classFilters: vansConfig.modelIdentifier,
          vehicleCategory: vansConfig.vehicleCategory ?? vehicleCategoryCommercial,
          campaigns: parameter.campaigns ?? [],
        };
      } else {
        return {
          ...parameter,
          brand: parameter.brand as string,
          bodyType: parameter.bodyType ?? bodyType,
          fuelType: vansConfig.fuelType,
          modelIdentifier: vansConfig.modelIdentifier,
          vehicleCategory: vansConfig.vehicleCategory ?? vehicleCategoryCommercial,
          campaigns: parameter.campaigns ?? [],
        };
      }
    }
  }
  if (isOneSearchPreferenceType(parameter)) {
    return {
      ...parameter,
      brandFilters: parameter.brandFilters,
      vehicleCategory: vehicleCategoryPrivate,
    };
  } else {
    return {
      ...parameter,
      brand: parameter.brand,
      vehicleCategory: vehicleCategoryPrivate,
    };
  }
}

export function getRequestGraphQLConfiguration(
  isCommercialVan: boolean,
  parameter: OneSearchVehicleTileDataParams,
): OneSearchVehicleTileDataParams {
  if (isCommercialVan) {
    const vansConfig = createVanConfiguration(parameter);

    const { body } = splitModelIdentifier(vansConfig.modelIdentifier);
    const bodyType = vansConfig.bodyType ?? body;

    if ('vehicleCategory' in vansConfig) {
      if (isOneSearchGraphQlType(parameter)) {
        return {
          ...parameter,
          brand: parameter.brand,
          bodyType: [bodyType],
          fuelType: [vansConfig.fuelType],
          modelIdentifier: [vansConfig.modelIdentifier],
          vehicleCategory: vansConfig.vehicleCategory ?? vehicleCategoryCommercial,
        };
      } else {
        return parameter;
      }
    }
  }
  return {
    ...parameter,
    brand: parameter.brand,
    vehicleCategory: vehicleCategoryPrivate,
  };
}
